import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { StaticQuery, graphql } from 'gatsby';
import Swiper from 'react-id-swiper';
import { gsap } from 'gsap';

//Components
import Image from './image';
import useObserver from './useObserver';

const title = {
  en: 'Sectors we have experience in',
  de: 'Sektoren, auf die wir spezialisiert sind',
  bg: 'Сектори',
};

const Slide = ({ slideData }) => (
  <div className="swiper-slide">
    <article className="slider__slide row align-items-start">
      <div className="col-lg-5 pt-lg-7 pr-xl-9 pr-mg-15 order-2 order-lg-1">
        <h5 className="slider__slide-title text-display pb-7 mb-7">
          {slideData.title}
        </h5>

        <div className="markdown-preview">
          <ReactMarkdown source={slideData.entry} />
        </div>
      </div>

      <div className="slider__slide-image col-lg-7 order-1 order-lg-2 mb-7 mb-lg-0">
        <Image
          className="mb-lg-6"
          image={slideData.image}
          alt={slideData.title}
        />
      </div>
    </article>
  </div>
);

const Slider = props => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const [swiper, updateSwiper] = useState(null);
  const [ref, setRef, animate] = useObserver({
    init: true,
    root: null,
    rootMargin: `-20% 0% -20% 0%`,
  });

  const goToNextSlide = () => {
    if (swiper !== null) {
      swiper.slideNext();
      setCurrentSlide(currentSlide + 1);
    }
  };

  const goToPrevSlide = () => {
    if (swiper !== null) {
      swiper.slidePrev();
      setCurrentSlide(currentSlide - 1);
    }
  };

  const params = {
    getSwiper: updateSwiper,
    slidesPerView: 1,
    allowTouchMove: false,
    spaceBetween: 40,
    effect: 'fade',
  };

  useEffect(() => {
    if (ref && window.innerWidth > 767) {
      gsap.set(ref, {
        opacity: 0,
        yPercent: 25,
      });
    }
  }, [ref]);

  useEffect(() => {
    if (animate && window.innerWidth > 767) {
      gsap.to(ref, {
        opacity: 1,
        yPercent: 0,
        duration: 0.6,
        ease: 'power0.easeNone',
      });
    }
  }, [animate]);

  return (
    <section
      ref={setRef}
      className="slider-sectors py-7 py-md-12 pt-lg-15 pt-mg-17 pb-xl-6 pb-mg-8 mb-md-8 mb-lg-10 mb-mg-17"
    >
      <div className="container position-relative">
        <h4 className="slider__title text-mega text-md-center mb-4 mb-md-7">
          {title[props.lang]}
        </h4>

        <Swiper {...params}>
          {props.slides.map(sector => (
            <Slide key={sector.title} slideData={sector} />
          ))}
        </Swiper>

        <div className="slider__controls d-flex align-items-center justify-content-start mt-5 mt-lg-0">
          <button
            className="btn btn-primary btn-rounded"
            disabled={currentSlide === 1}
            onClick={goToPrevSlide}
          >
            <i className="far fa-long-arrow-left" aria-hidden="true"></i>
          </button>

          <p className="text-headline text-center mx-5">
            {currentSlide}/{props.slides.length}
          </p>

          <button
            className="btn btn-primary btn-rounded"
            disabled={currentSlide === props.slides.length}
            onClick={goToNextSlide}
          >
            <i className="far fa-long-arrow-right" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </section>
  );
};

const SliderSectors = ({ lang, cmsPreview }) => {
  if (cmsPreview) {
    return (
      <section className="py-8">
        <div className="container">
          <p className="text-title text-center">
            Sectors slider will be generated based on the "Sectors" entered in
            the "Sectors Page"
          </p>
        </div>
      </section>
    );
  }

  return (
    <StaticQuery
      query={graphql`
        query SECTORS_LIST_QUERY {
          pagesJson(pageKey: { eq: "sectors" }) {
            languages {
              en {
                sectors {
                  title
                  entry
                  image {
                    childImageSharp {
                      fluid(maxWidth: 900) {
                        ...GatsbyImageSharpFluid_withWebp
                      }
                    }
                    publicURL
                  }
                }
              }
              de {
                sectors {
                  title
                  entry
                  image {
                    childImageSharp {
                      fluid(maxWidth: 900) {
                        ...GatsbyImageSharpFluid_withWebp
                      }
                    }
                    publicURL
                  }
                }
              }
              bg {
                sectors {
                  title
                  entry
                  image {
                    childImageSharp {
                      fluid(maxWidth: 900) {
                        ...GatsbyImageSharpFluid_withWebp
                      }
                    }
                    publicURL
                  }
                }
              }
            }
          }
        }
      `}
      render={({ pagesJson: data }) => {
        if (
          !data.languages[lang].sectors ||
          !data.languages[lang].sectors.length
        ) {
          return null;
        }

        return <Slider slides={data.languages[lang].sectors} lang={lang} />;
      }}
    />
  );
};

export default SliderSectors;
